import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Compass, ArrowRight, ArrowCounterClockwise, X, Check } from "@phosphor-icons/react";
import { ProductOverview } from "@/components/ProductInfoModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function recommend(category, a) {
  if (category === "commercial_real_estate") {
    if (a.plan === "build") return "Construction";
    if (a.plan === "flip") return "Fix & Flip";
    if (a.plan === "hold") return "Flip & Hold";
    if (a.speed === "fast") return a.credit === "yes" ? "Hard Money" : "Bridge";
    return a.plan === "operate" ? "SBA 504" : "Conventional CRE";
  }
  if (a.use === "equipment") return "Equipment";
  if (a.use === "cushion") return "Line of Credit";
  if (a.use === "expansion") return a.priority === "cost" ? "SBA 7(a)" : "Term Loan";
  if (a.priority === "cost") return "SBA 7(a)";
  return a.cards === "yes" ? "Merchant Cash Advance" : "Working Capital";
}

function questionsFor(category, a) {
  if (!category) {
    return { key: "category", title: "What are you financing?", options: [
      { v: "commercial_real_estate", label: "Real estate", desc: "Buy, refinance, build, or renovate property" },
      { v: "business", label: "My business", desc: "Working capital, equipment, expansion, cash flow" },
    ]};
  }
  if (category === "commercial_real_estate") {
    if (!a.plan) return { key: "plan", title: "What's your plan for the property?", options: [
      { v: "operate", label: "Run my business from it", desc: "Owner-occupied — offices, shop, warehouse" },
      { v: "invest", label: "Buy or refinance an investment property", desc: "Rental or income-producing asset" },
      { v: "flip", label: "Renovate it and sell it", desc: "Fix & flip for profit" },
      { v: "hold", label: "Renovate it and rent it out", desc: "Build a rental portfolio" },
      { v: "build", label: "Build new", desc: "Ground-up construction or major rehab" },
    ]};
    if (["operate", "invest"].includes(a.plan) && !a.speed) return { key: "speed", title: "How fast do you need to close?", options: [
      { v: "fast", label: "As fast as possible", desc: "Under ~3 weeks — time-sensitive deal" },
      { v: "normal", label: "A standard timeline is fine", desc: "I want the best long-term pricing" },
    ]};
    if (a.speed === "fast" && !a.credit) return { key: "credit", title: "Any credit challenges or an unusual property?", options: [
      { v: "yes", label: "Yes", desc: "Credit dings or a property banks shy away from" },
      { v: "no", label: "No", desc: "Solid credit and a conventional property" },
    ]};
    return null;
  }
  if (!a.use) return { key: "use", title: "What will you use the funds for?", options: [
    { v: "equipment", label: "Equipment or vehicles", desc: "Machinery, trucks, technology" },
    { v: "cashflow", label: "Day-to-day cash flow", desc: "Payroll, inventory, seasonal gaps" },
    { v: "cushion", label: "A flexible cushion", desc: "Draw funds only when I need them" },
    { v: "expansion", label: "A one-time investment", desc: "Expansion, renovation, acquisition" },
  ]};
  if (["cashflow", "expansion"].includes(a.use) && !a.priority) return { key: "priority", title: "What matters most to you?", options: [
    { v: "cost", label: "Lowest cost", desc: "I can wait a few weeks for the best rate" },
    { v: "speed", label: "Speed", desc: "I need the funds fast" },
  ]};
  if (a.use === "cashflow" && a.priority === "speed" && !a.cards) return { key: "cards", title: "Do most sales come through cards or daily revenue?", options: [
    { v: "yes", label: "Yes", desc: "Strong card or daily sales volume" },
    { v: "no", label: "No", desc: "Mostly invoices or irregular revenue" },
  ]};
  return null;
}

export default function ProductRecommender({ category: presetCategory = "", onPick, applyLink = "", onClose, source = "homepage" }) {
  const [category, setCategory] = useState(presetCategory);
  const [answers, setAnswers] = useState({});
  const [leadEmail, setLeadEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState("idle");
  const postedRef = useRef(false);

  const q = useMemo(() => questionsFor(category, answers), [category, answers]);
  const result = q ? null : recommend(category, answers);

  useEffect(() => {
    if (result && !postedRef.current) {
      postedRef.current = true;
      axios.post(`${API}/public/recommender-events`, { source, category, product: result, answers }).catch(() => {});
    }
  }, [result, source, category, answers]);

  function answer(key, v) {
    if (key === "category") setCategory(v);
    else setAnswers((p) => ({ ...p, [key]: v }));
  }

  function reset() {
    setAnswers({});
    setCategory(presetCategory);
    setLeadEmail("");
    setLeadStatus("idle");
    postedRef.current = false;
  }

  async function submitLead(e) {
    e.preventDefault();
    const email = leadEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadStatus("invalid");
      return;
    }
    setLeadStatus("sending");
    try {
      await axios.post(`${API}/public/recommender-leads`, { email, source, category, product: result, answers });
      setLeadStatus("done");
    } catch {
      setLeadStatus("error");
    }
  }

  const body = (
    <div data-testid="product-recommender">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[3px]" style={{ color: "var(--muted-fg, #5A6478)" }}>
            <Compass size={12} style={{ display: "inline", verticalAlign: "-2px" }} /> Find your loan
          </p>
          <h3 className="font-display text-xl mt-1" style={{ color: "var(--ink, #0E1525)", fontWeight: 400 }}>
            {result ? "Our suggestion for you" : q.title}
          </h3>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close" style={{ color: "var(--muted-fg, #5A6478)" }} data-testid="recommender-close">
            <X size={18} />
          </button>
        )}
      </div>

      {!result ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
          {q.options.map((o) => (
            <button
              key={o.v}
              type="button"
              className="text-left p-3.5 transition-colors"
              style={{ background: "var(--surface, #fff)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 6 }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--primary, #1F5DC6)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border, #D9E0EB)"; }}
              onClick={() => answer(q.key, o.v)}
              data-testid={`recommender-opt-${o.v}`}
            >
              <p className="text-sm font-medium" style={{ color: "var(--ink, #0E1525)" }}>{o.label}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--muted-fg, #5A6478)" }}>{o.desc}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-4" data-testid="recommender-result">
          <p className="font-display text-3xl font-light" style={{ color: "var(--primary, #1F5DC6)" }} data-testid="recommender-product">
            {result}
          </p>
          <ProductOverview product={result} compact />
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {onPick && (
              <button type="button" className="btn-primary" onClick={() => onPick(result, category)} data-testid="recommender-use-btn">
                <Check size={15} /> Use this product
              </button>
            )}
            {applyLink && (
              <Link to={applyLink} className="btn-primary" data-testid="recommender-apply-btn">
                Apply with this product <ArrowRight size={14} />
              </Link>
            )}
            <button type="button" className="btn-ghost text-xs" onClick={reset} data-testid="recommender-restart-btn">
              <ArrowCounterClockwise size={13} /> Start over
            </button>
          </div>
          <p className="text-[11px] mt-3" style={{ color: "var(--muted-fg, #5A6478)" }}>
            This is a starting point, not a commitment — our team confirms the best fit with you.
          </p>
          {source !== "application" && (
            <div className="mt-4 p-3.5" style={{ background: "var(--bg, #F5F7FB)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 6 }} data-testid="recommender-lead-box">
              {leadStatus === "done" ? (
                <p className="text-sm font-medium" style={{ color: "#1E7D50" }} data-testid="recommender-lead-done">
                  <Check size={14} style={{ display: "inline", verticalAlign: "-2px" }} /> Thanks — a financing specialist will reach out shortly.
                </p>
              ) : (
                <>
                  <p className="text-xs font-medium" style={{ color: "var(--ink, #0E1525)" }}>
                    Want a specialist to walk you through {result}? Leave your email and we'll reach out.
                  </p>
                  <form onSubmit={submitLead} className="flex items-stretch gap-2 mt-2 flex-wrap sm:flex-nowrap">
                    <input
                      type="email"
                      value={leadEmail}
                      onChange={(e) => { setLeadEmail(e.target.value); if (leadStatus !== "idle") setLeadStatus("idle"); }}
                      placeholder="you@company.com"
                      className="flex-1 min-w-[180px] px-3 py-2 text-sm"
                      style={{ background: "var(--surface, #fff)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 4 }}
                      data-testid="recommender-lead-email"
                    />
                    <button type="submit" className="btn-primary text-xs whitespace-nowrap" disabled={leadStatus === "sending"} data-testid="recommender-lead-submit">
                      {leadStatus === "sending" ? "Sending…" : "Contact me"}
                    </button>
                  </form>
                  {leadStatus === "invalid" && (
                    <p className="text-[11px] mt-1.5" style={{ color: "#B42318" }} data-testid="recommender-lead-invalid">Please enter a valid email address.</p>
                  )}
                  {leadStatus === "error" && (
                    <p className="text-[11px] mt-1.5" style={{ color: "#B42318" }} data-testid="recommender-lead-error">Something went wrong — please try again.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (onClose) {
    return (
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        style={{ background: "rgba(14, 21, 37, 0.55)", backdropFilter: "blur(3px)" }}
        onClick={onClose}
        data-testid="recommender-modal"
      >
        <div
          className="w-full max-w-xl p-7"
          style={{ background: "var(--surface, #fff)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 6, maxHeight: "85vh", overflowY: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          {body}
        </div>
      </div>
    );
  }
  return body;
}
